import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import service from '../../store/store/baseapiservice';

interface EFileManagementDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  currentLanguage: string;
}

interface CaptchaData {
  cptch_id: string;
  salt_ky: string;
  cptch_img_src_tx: string;
}

interface FileDetails {
  tckt_id?: number;
  [key: string]: any;
}

export default function EFileManagementDrawer({ isOpen, onClose, currentLanguage }: EFileManagementDrawerProps) {
  const [fileReferenceNumber, setFileReferenceNumber] = useState('');
  const [captchaCode, setCaptchaCode] = useState('');
  const [captchaData, setCaptchaData] = useState<CaptchaData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCaptcha, setIsLoadingCaptcha] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileDetails, setFileDetails] = useState<FileDetails | null>(null);

  // Fetch captcha when drawer opens
  useEffect(() => {
    if (isOpen) {
      fetchCaptcha();
      // Reset form
      setFileReferenceNumber('');
      setCaptchaCode('');
      setError(null);
      setFileDetails(null);
    }
  }, [isOpen]);

  const fetchCaptcha = async () => {
    try {
      setIsLoadingCaptcha(true);
      const response: any = await service.get('/auth2/ss/login/capcha');
      if (response && response.status === 200 && response.data) {
        setCaptchaData({
          cptch_id: response.data.cptch_id,
          salt_ky: response.data.salt_ky,
          cptch_img_src_tx: response.data.cptch_img_src_tx
        });
        setError(null);
      } else {
        setError(response?.message || (currentLanguage === 'en' 
          ? 'Failed to load captcha. Please try again.' 
          : 'కాప్చాను లోడ్ చేయడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
      }
    } catch (err: any) {
      console.error('Error fetching captcha:', err);
      setError(err.message || (currentLanguage === 'en' 
        ? 'Failed to load captcha. Please try again.' 
        : 'కాప్చాను లోడ్ చేయడంలో విఫలమైంది. దయచేసి మళ్లీ ప్రయత్నించండి.'));
    } finally {
      setIsLoadingCaptcha(false);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!fileReferenceNumber.trim()) {
      setError(currentLanguage === 'en' 
        ? 'Please enter File Reference Number' 
        : 'దయచేసి ఫైల్ రిఫరెన్స్ నంబర్ నమోదు చేయండి');
      return;
    }
    
    if (!captchaCode.trim()) {
      setError(currentLanguage === 'en' 
        ? 'Please enter valid captcha' 
        : 'దయచేసి చెల్లుబాటు అయ్యే కాప్చా నమోదు చేయండి');
      return;
    }

    if (!captchaData) {
      setError(currentLanguage === 'en' 
        ? 'Captcha not loaded. Please refresh and try again.' 
        : 'కాప్చా లోడ్ కాలేదు. దయచేసి రిఫ్రెష్ చేసి మళ్లీ ప్రయత్నించండి.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Check if user is logged in
      const citizenName = localStorage.getItem('citizenName');
      let route = '';
      
      if (citizenName) {
        route = `/public/web/file/ticket/${fileReferenceNumber.trim()}`;
      } else {
        route = `/open/file/ticket/${fileReferenceNumber.trim()}`;
      }

      const postData = {
        captcha: captchaCode.trim(),
        captchaID: captchaData.cptch_id
      };

      const response: any = await service.post(route, postData);

      if (response && response.status === 200) {
        if (response.data && Array.isArray(response.data) && response.data.length > 0) {
          setFileDetails(response.data[0]);
          setError(null);
        } else {
          // Refresh captcha on error
          await fetchCaptcha();
          setCaptchaCode('');
          setError(currentLanguage === 'en' 
            ? 'Enter Valid Reference Number' 
            : 'చెల్లుబాటు అయ్యే రిఫరెన్స్ నంబర్ నమోదు చేయండి');
        }
      } else if (response?.status === 411 || response?.status === 601) {
        await fetchCaptcha();
        setCaptchaCode('');
        setError(response?.message || (currentLanguage === 'en' 
          ? 'Invalid captcha. Please try again.' 
          : 'చెల్లని కాప్చా. దయచేసి మళ్లీ ప్రయత్నించండి.'));
      } else if (response?.status === 406 || response?.status === 417) {
        await fetchCaptcha();
        setCaptchaCode('');
        setError(response?.message || (currentLanguage === 'en' 
          ? 'Invalid captcha. Please try again.' 
          : 'చెల్లని కాప్చా. దయచేసి మళ్లీ ప్రయత్నించండి.'));
      } else {
        await fetchCaptcha();
        setCaptchaCode('');
        setError(response?.message || (currentLanguage === 'en' 
          ? 'Check your internet and try again...' 
          : 'మీ ఇంటర్నెట్‌ను తనిఖీ చేసి మళ్లీ ప్రయత్నించండి...'));
      }
    } catch (err: any) {
      console.error('Error searching file:', err);
      await fetchCaptcha();
      setCaptchaCode('');
      setError(err.message || (currentLanguage === 'en' 
        ? 'Something went wrong. Please contact the administrator.' 
        : 'ఏదో తప్పు జరిగింది. దయచేసి నిర్వాహకుడిని సంప్రదించండి.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[10000]"
          />

          {/* Drawer */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl z-[10000] flex flex-col overflow-y-auto"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-500 to-amber-600 text-white px-5 py-4 flex items-center justify-between shadow sticky top-0 z-10">
              <p className="text-sm font-bold uppercase tracking-wide flex-1">
                eFile Management
              </p>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-100 transition p-1"
                aria-label="Close form"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Form Content */}
            <div className="flex-1 p-5 space-y-4">
              <form onSubmit={handleSearch} className="space-y-4">
                {/* File Reference Number */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {currentLanguage === 'en' ? 'File Reference Number' : 'ఫైల్ రిఫరెన్స్ నంబర్'}
                  </label>
                  <div className="flex items-center">
                    <div className="px-3 py-2 bg-gray-100 border border-r-0 border-gray-300 rounded-l-lg">
                      <span className="text-gray-600 font-medium">#</span>
                    </div>
                    <input
                      type="text"
                      value={fileReferenceNumber}
                      onChange={(e) => setFileReferenceNumber(e.target.value)}
                      placeholder={currentLanguage === 'en' 
                        ? 'Please enter File Reference Number' 
                        : 'దయచేసి ఫైల్ రిఫరెన్స్ నంబర్ నమోదు చేయండి'}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      required
                    />
                  </div>
                </div>

                {/* Captcha Section */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 bg-gray-100 border border-gray-300 rounded-lg p-3 flex items-center justify-center min-h-[60px]">
                      {isLoadingCaptcha ? (
                        <div className="flex items-center gap-2">
                          <i className="ri-loader-4-line animate-spin text-gray-400"></i>
                          <span className="text-xs text-gray-500">
                            {currentLanguage === 'en' ? 'Loading captcha...' : 'కాప్చా లోడ్ అవుతోంది...'}
                          </span>
                        </div>
                      ) : captchaData ? (
                        <img 
                          src={captchaData.cptch_img_src_tx} 
                          alt="Captcha" 
                          className="max-h-12"
                        />
                      ) : (
                        <span className="text-xs text-gray-500">
                          {currentLanguage === 'en' ? 'Captcha not available' : 'కాప్చా అందుబాటులో లేదు'}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={fetchCaptcha}
                      disabled={isLoadingCaptcha}
                      className="px-3 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      aria-label="Refresh captcha"
                    >
                      <i className="ri-refresh-line text-lg"></i>
                    </button>
                  </div>
                  
                  <label className="block text-sm font-medium text-gray-700 mb-2 mt-3">
                    {currentLanguage === 'en' 
                      ? 'Enter the code from the above image' 
                      : 'పై చిత్రం నుండి కోడ్‌ను నమోదు చేయండి'}
                  </label>
                  <input
                    type="text"
                    value={captchaCode}
                    onChange={(e) => setCaptchaCode(e.target.value)}
                    placeholder={currentLanguage === 'en' ? 'Enter captcha code' : 'కాప్చా కోడ్ నమోదు చేయండి'}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                    autoComplete="off"
                    required
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {/* File Details */}
                {fileDetails && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="mb-3">
                      <h4 className="text-sm font-semibold text-green-800 mb-2">
                        {currentLanguage === 'en' ? 'File Details' : 'ఫైల్ వివరాలు'}
                      </h4>
                      <div className="text-sm font-bold text-green-900 mb-2">
                        {currentLanguage === 'en' ? 'TICKET NO:' : 'టికెట్ నంబర్:'} #{fileDetails.tckt_id || 'N/A'}
                      </div>
                    </div>
                    <div className="space-y-2 text-sm text-gray-700">
                      {Object.entries(fileDetails).map(([key, value]) => {
                        if (key === 'tckt_id') return null;
                        return (
                          <div key={key} className="flex justify-between">
                            <span className="font-medium text-gray-600">{key}:</span>
                            <span className="text-gray-900">{String(value)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Search Button */}
                <div className="pt-4">
                  <motion.button
                    type="submit"
                    disabled={isLoading || isLoadingCaptcha || !captchaData}
                    className="w-full bg-gradient-to-r from-indigo-600 to-violet-700 hover:from-indigo-700 hover:to-violet-800 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: isLoading ? 1 : 1.02 }}
                    whileTap={{ scale: isLoading ? 1 : 0.98 }}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>
                        {currentLanguage === 'en' ? 'Searching...' : 'శోధిస్తోంది...'}
                      </span>
                    ) : (
                      'SEARCH'
                    )}
                  </motion.button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

